import {
  AIInsight,
  AnomalyDetection,
  MaintenancePrediction,
  DeviceHealthScore,
  ChatMessage,
  EnergyOptimization,
} from '@/types/ai';

class AIService {
  // Mock anomaly detection using statistical methods
  async detectAnomalies(
    sensorData: Array<{ value: number; timestamp: Date; deviceId: string; sensorType: string }>,
  ): Promise<AnomalyDetection[]> {
    const anomalies: AnomalyDetection[] = [];

    // Group by device and sensor type
    const grouped = sensorData.reduce(
      (acc, item) => {
        const key = `${item.deviceId}_${item.sensorType}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      },
      {} as Record<string, typeof sensorData>,
    );

    Object.entries(grouped).forEach(([key, data]) => {
      const [deviceId, sensorType] = key.split('_');
      const values = data.map((d) => d.value);
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const stdDev = Math.sqrt(values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length);

      // Find outliers (values beyond 2 standard deviations)
      data.forEach((item) => {
        const deviation = Math.abs(item.value - mean) / stdDev;
        if (deviation > 2) {
          anomalies.push({
            id: `anomaly_${Date.now()}_${Math.random()}`,
            deviceId,
            sensorType,
            value: item.value,
            expectedRange: { min: mean - 2 * stdDev, max: mean + 2 * stdDev },
            deviation,
            timestamp: item.timestamp,
            severity: deviation > 3 ? 'high' : deviation > 2.5 ? 'medium' : 'low',
            isResolved: false,
          });
        }
      });
    });

    return anomalies;
  }

  // Smart real-time anomaly detection with statistical analysis
  async detectRealTimeAnomalies(
    timeSeries: Array<{ timestamp: string; value: number; sensor_type: string; device_id: string }>,
    windowSize: number = 24,
  ): Promise<AnomalyDetection[]> {
    const anomalies: AnomalyDetection[] = [];

    // Group data by sensor type and device
    const groupedData = timeSeries.reduce(
      (acc, point) => {
        const key = `${point.device_id}_${point.sensor_type}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(point);
        return acc;
      },
      {} as Record<string, typeof timeSeries>,
    );

    Object.entries(groupedData).forEach(([key, data]) => {
      const [deviceId, sensorType] = key.split('_');

      // Sort by timestamp
      const sortedData = data.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      // Apply sliding window analysis
      for (let i = windowSize; i < sortedData.length; i++) {
        const window = sortedData.slice(i - windowSize, i);
        const currentValue = sortedData[i].value;

        // Calculate statistical measures
        const values = window.map((p) => p.value);
        const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
        const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);

        // Z-score anomaly detection
        const zScore = Math.abs(currentValue - mean) / stdDev;

        // Seasonal decomposition (simplified)
        const seasonality = this.detectSeasonality(window, sensorType);
        const trend = this.calculateTrend(window);

        // Combined anomaly score
        const anomalyScore = zScore + seasonality.anomalyFactor + trend.anomalyFactor;

        if (anomalyScore > 2.5) {
          // Threshold for anomaly
          anomalies.push({
            id: `anomaly_${Date.now()}_${Math.random()}`,
            deviceId,
            sensorType,
            value: currentValue,
            expectedRange: {
              min: mean - 2 * stdDev,
              max: mean + 2 * stdDev,
            },
            deviation: zScore,
            timestamp: new Date(sortedData[i].timestamp),
            severity: zScore > 4 ? 'high' : zScore > 3 ? 'medium' : 'low',
            isResolved: false,
          });
        }
      }
    });

    return anomalies;
  }

  // Generate AI insights based on data patterns
  async generateInsights(devices: any[], sensorData: any[]): Promise<AIInsight[]> {
    const insights: AIInsight[] = [];

    // Mock insights generation
    if (devices.length > 0) {
      insights.push({
        id: 'insight_1',
        type: 'trend',
        title: 'Temperature Rising Trend Detected',
        description: `Average temperature across ${devices.length} devices has increased by 12% over the last 24 hours. This could indicate HVAC system inefficiency.`,
        confidence: 87,
        severity: 'medium',
        actionable: true,
        suggestedActions: [
          'Check HVAC system filters',
          'Verify thermostat settings',
          'Inspect insulation around sensors',
        ],
        affectedDevices: devices.slice(0, 3).map((d) => d.id),
        timestamp: new Date(),
        metadata: { trendPercentage: 12, timeframe: '24h' },
      });

      insights.push({
        id: 'insight_2',
        type: 'optimization',
        title: 'Energy Optimization Opportunity',
        description:
          'AI analysis suggests 23% energy savings possible by adjusting device schedules based on usage patterns.',
        confidence: 92,
        severity: 'low',
        actionable: true,
        suggestedActions: [
          'Implement smart scheduling',
          'Enable power-saving modes during low usage',
          'Optimize sensor polling intervals',
        ],
        affectedDevices: devices.map((d) => d.id),
        timestamp: new Date(),
        metadata: { potentialSavings: 23 },
      });

      insights.push({
        id: 'insight_3',
        type: 'anomaly',
        title: 'Unusual Activity Pattern',
        description:
          'Device DEV-001 showing irregular sensor readings. Pattern differs from historical baseline by 300%.',
        confidence: 95,
        severity: 'high',
        actionable: true,
        suggestedActions: [
          'Inspect device for physical damage',
          'Check sensor calibration',
          'Verify network connectivity',
        ],
        affectedDevices: [devices[0]?.id].filter(Boolean),
        timestamp: new Date(),
        metadata: { deviationPercentage: 300 },
      });
    }

    return insights;
  }

  // Advanced pattern recognition for insights
  async generateAdvancedInsights(
    devices: any[],
    sensorData: Array<{ timestamp: string; value: number; sensor_type: string; device_id: string; unit?: string }>,
    timeRange: string = '24h',
  ): Promise<AIInsight[]> {
    const insights: AIInsight[] = [];

    // Performance trend analysis
    const performanceTrends = this.analyzePerformanceTrends(sensorData, devices);
    insights.push(...performanceTrends);

    // Cross-correlation analysis
    const correlationInsights = this.analyzeCrossCorrelations(sensorData);
    insights.push(...correlationInsights);

    // Efficiency optimization opportunities
    const optimizationOpportunities = await this.identifyOptimizationOpportunities(sensorData, devices);
    insights.push(...optimizationOpportunities);

    // Predictive failure analysis
    const failurePredictions = this.predictFailures(sensorData, devices);
    insights.push(...failurePredictions);

    return insights;
  }

  // Predict maintenance needs
  async predictMaintenance(devices: any[]): Promise<MaintenancePrediction[]> {
    return devices.map((device) => {
      const daysUntilMaintenance = Math.floor(Math.random() * 90) + 10;
      const failureProbability = Math.random() * 0.4 + 0.1; // 10-50%

      return {
        deviceId: device.id,
        component: ['Sensor Array', 'Power Supply', 'Communication Module', 'Housing'][Math.floor(Math.random() * 4)],
        failureProbability,
        predictedFailureDate: new Date(Date.now() + daysUntilMaintenance * 24 * 60 * 60 * 1000),
        daysUntilMaintenance,
        recommendedActions: [
          'Schedule preventive maintenance',
          'Order replacement parts',
          'Monitor performance closely',
        ],
        confidence: Math.random() * 0.3 + 0.7, // 70-100%
        priority: failureProbability > 0.3 ? 'high' : failureProbability > 0.2 ? 'medium' : 'low',
      };
    });
  }

  // Calculate device health scores
  async calculateHealthScores(devices: any[]): Promise<DeviceHealthScore[]> {
    return devices.map((device) => {
      const overallHealth = Math.floor(Math.random() * 40) + 60; // 60-100

      return {
        deviceId: device.id,
        overallHealth,
        componentHealth: {
          sensors: Math.floor(Math.random() * 30) + 70,
          connectivity: Math.floor(Math.random() * 25) + 75,
          power: Math.floor(Math.random() * 35) + 65,
          processing: Math.floor(Math.random() * 20) + 80,
        },
        trending: overallHealth > 85 ? 'improving' : overallHealth < 70 ? 'declining' : 'stable',
        lastUpdated: new Date(),
      };
    });
  }

  // Energy optimization
  async optimizeEnergy(devices: any[]): Promise<EnergyOptimization[]> {
    return devices.map((device) => {
      const currentConsumption = 100 + Math.random() * 200; // 100-300W
      const savingsPercentage = Math.random() * 30 + 10; // 10-40%
      const optimizedConsumption = currentConsumption * (1 - savingsPercentage / 100);
      const potentialSavings = currentConsumption - optimizedConsumption;

      return {
        deviceId: device.id,
        currentConsumption,
        optimizedConsumption,
        potentialSavings,
        savingsPercentage,
        optimizationActions: [
          {
            action: 'Optimize polling frequency',
            impact: Math.round(savingsPercentage * 0.4),
            difficulty: 'easy' as const,
            estimatedSavings: Math.round(potentialSavings * 0.4),
          },
          {
            action: 'Enable sleep mode',
            impact: Math.round(savingsPercentage * 0.3),
            difficulty: 'medium' as const,
            estimatedSavings: Math.round(potentialSavings * 0.3),
          },
          {
            action: 'Upgrade firmware',
            impact: Math.round(savingsPercentage * 0.3),
            difficulty: 'hard' as const,
            estimatedSavings: Math.round(potentialSavings * 0.3),
          },
        ],
      };
    });
  }

  // Process natural language queries with basic responses
  async processBasicQuery(lowerQuery: string): Promise<ChatMessage> {
    let response = "I'm analyzing your query. Here's what I found based on your IoT data...";
    let attachments: any[] = [];

    // Smart responses with more context
    if (lowerQuery.includes('temperature')) {
      response =
        '📊 **Temperature Analysis:**\n\nCurrent average: 24.3°C across all devices\n• Greenhouse sensors: 28.1°C (↑2.3°)\n• Indoor sensors: 22.5°C (stable)\n• Outdoor sensors: 19.8°C (↓1.1°)\n\n⚠️ Greenhouse temperature trending upward - consider ventilation check.';

      if (lowerQuery.includes('chart') || lowerQuery.includes('trend')) {
        attachments.push({
          type: 'chart',
          data: {
            type: 'line',
            title: 'Temperature Trends (Last 24 Hours)',
            data: Array.from({ length: 24 }, (_, i) => ({
              time: `${i}:00`,
              greenhouse: 26 + Math.sin(i / 6) * 3 + Math.random() * 2,
              indoor: 22 + Math.sin(i / 8) * 1.5 + Math.random() * 1,
              outdoor: 18 + Math.sin(i / 4) * 4 + Math.random() * 2,
            })),
            unit: '°C',
          },
        });
      }
    } else if (lowerQuery.includes('humidity')) {
      response =
        '💧 **Humidity Analysis:**\n\nSystem-wide humidity levels are mostly optimal:\n• Average: 52% RH\n• Range: 35-68% RH\n• 3 devices below optimal (< 40%)\n• 1 device above optimal (> 65%)\n\n💡 Recommendation: Check ventilation in high-humidity areas.';
    } else if (lowerQuery.includes('alert') || lowerQuery.includes('alarm')) {
      response =
        '🚨 **Current Alerts Summary:**\n\n**High Priority (1):**\n• DEV-003: Temperature anomaly detected\n\n**Medium Priority (2):**\n• DEV-007: Connectivity intermittent\n• DEV-012: Battery level low (15%)\n\n**Low Priority (3):**\n• Maintenance due for 3 devices\n\n📋 All alerts are being monitored. Critical issues have been escalated.';
    } else if (lowerQuery.includes('energy') || lowerQuery.includes('power') || lowerQuery.includes('consumption')) {
      response =
        '⚡ **Energy Analysis:**\n\nCurrent consumption: 2.4 kW across all devices\n• 15% below monthly average\n• Peak consumption: 3.1 kW (2:00 PM)\n• Lowest consumption: 1.8 kW (4:00 AM)\n\n💰 **Optimization Opportunities:**\n• Smart scheduling: Save up to 18%\n• Power mode optimization: Save up to 12%\n• Sensor polling reduction: Save up to 8%';
    }

    return {
      id: `msg_${Date.now()}`,
      type: 'assistant',
      content: response,
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };
  }

  // Process natural language queries with smart analytics integration
  async processQuery(query: string): Promise<ChatMessage> {
    const lowerQuery = query.toLowerCase();
    let response = "I'm analyzing your query. Here's what I found based on your IoT data...";
    let attachments: any[] = [];

    // Smart analytics queries with visual data trends
    if (lowerQuery.includes('anomal') || lowerQuery.includes('alert')) {
      response =
        '🚨 **Anomaly Detection Analysis:**\n\n**Current Anomalies Found:**\n• Temperature spike detected at 14:30 (45.2°C)\n• Humidity drop below 20% at Sensor-003\n• Unusual pressure reading at Gateway-07\n\n**AI Insights:**\n• Pattern suggests HVAC malfunction\n• 85% confidence in temperature sensor accuracy\n• Recommend immediate system check\n\n**Severity Breakdown:**\n• High: 1 anomaly\n• Medium: 2 anomalies\n• Low: 0 anomalies';

      attachments.push({
        type: 'chart',
        data: {
          type: 'timeline',
          title: 'Real-time Anomaly Detection',
          data: [
            { time: '2h ago', type: 'Temperature Spike', severity: 'high', device: 'Sensor-001', value: 45.2 },
            { time: '4h ago', type: 'Humidity Drop', severity: 'medium', device: 'Sensor-003', value: 15.8 },
            { time: '6h ago', type: 'Pressure Anomaly', severity: 'medium', device: 'Sensor-007', value: 1025.3 },
          ],
        },
      });
    } else if (lowerQuery.includes('correlation') || lowerQuery.includes('relationship')) {
      response =
        '🔗 **Advanced Correlation Analysis:**\n\n**Strong Correlations Detected:**\n• Temperature ↔ Humidity: -0.73 (strong inverse)\n• CO₂ ↔ Occupancy: +0.89 (very strong positive)\n• Light ↔ Energy Usage: +0.65 (moderate positive)\n• Pressure ↔ Weather: +0.52 (moderate positive)\n\n**Actionable Insights:**\n• HVAC efficiency optimization potential: 15%\n• Occupancy-based scheduling could save 12% energy\n• Natural lighting correlation suggests smart controls\n\n**Statistical Confidence: 94%**';

      attachments.push({
        type: 'chart',
        data: {
          type: 'scatter',
          title: 'Temperature vs Humidity Correlation Matrix',
          data: Array.from({ length: 50 }, (_, i) => ({
            temperature: 18 + Math.random() * 15,
            humidity: 70 - (18 + Math.random() * 15) * 1.8 + Math.random() * 10,
            device: `Device ${Math.floor(i / 10) + 1}`,
            timestamp: new Date(Date.now() - i * 60 * 60 * 1000),
          })),
          correlation: -0.73,
          xAxis: 'Temperature (°C)',
          yAxis: 'Humidity (%)',
        },
      });
    } else if (lowerQuery.includes('trend') || lowerQuery.includes('pattern')) {
      response =
        '📈 **Advanced Trend Analysis:**\n\n**Temperature Trends (24h):**\n• Average: 22.4°C (±2.1°C)\n• Peak: 26.8°C at 14:30\n• Minimum: 19.2°C at 05:00\n• Anomalies detected: 2 spikes\n\n**Pattern Recognition:**\n• Daily cycle: Normal sinusoidal pattern\n• Weekly trend: Stable baseline\n• Seasonal adjustment: +2°C vs last month\n\n**Predictive Insights:**\n• Next 6h forecast: 21-24°C\n• Confidence interval: 92%\n• Risk assessment: Low';

      attachments.push({
        type: 'chart',
        data: {
          type: 'area',
          title: 'Temperature Trends with Anomaly Detection',
          data: Array.from({ length: 48 }, (_, i) => {
            const baseTemp = 22 + Math.sin(i / 8) * 4;
            const anomaly = i === 15 || i === 32 ? 8 : 0;
            const value = baseTemp + anomaly + (Math.random() - 0.5) * 2;
            return {
              time: `${Math.floor(i / 2)}:${i % 2 === 0 ? '00' : '30'}`,
              temperature: value,
              normal_range_min: baseTemp - 3,
              normal_range_max: baseTemp + 3,
              anomaly: anomaly > 0 ? value : null,
              timestamp: new Date(Date.now() - (47 - i) * 30 * 60 * 1000),
            };
          }),
          unit: '°C',
          anomalies: [
            { time: '7:30', value: 30.2, severity: 'high' },
            { time: '16:00', value: 29.8, severity: 'medium' },
          ],
        },
      });
    } else if (
      lowerQuery.includes('energy') ||
      lowerQuery.includes('consumption') ||
      lowerQuery.includes('optimization')
    ) {
      response =
        '⚡ **Energy Optimization Analysis:**\n\n**Current Consumption:**\n• Total: 2.4 kW across 12 devices\n• Peak device: Server Room (380W)\n• Most efficient: Sensor Array (15W)\n\n**Optimization Opportunities:**\n• Smart scheduling: 18% savings potential\n• Power mode optimization: 12% reduction\n• Load balancing: 8% efficiency gain\n\n**Cost Analysis:**\n• Current monthly cost: $340\n• Optimized cost: $265 (-22%)\n• ROI timeline: 3.2 months';

      attachments.push({
        type: 'chart',
        data: {
          type: 'bar',
          title: 'Energy Consumption vs Optimization Potential',
          data: Array.from({ length: 8 }, (_, i) => {
            const current = 100 + Math.random() * 200;
            const optimized = current * (0.7 + Math.random() * 0.2);
            return {
              device: `Device ${i + 1}`,
              current: Math.round(current),
              optimized: Math.round(optimized),
              savings: Math.round(current - optimized),
              efficiency: Math.round((optimized / current) * 100),
            };
          }),
          unit: 'W',
        },
      });
    } else if (lowerQuery.includes('distribution') || lowerQuery.includes('sensor')) {
      response =
        '📊 **Sensor Distribution & Health Analysis:**\n\n**Device Distribution:**\n• Temperature sensors: 35% (18 devices)\n• Humidity sensors: 25% (13 devices)\n• Pressure sensors: 20% (10 devices)\n• Light sensors: 15% (8 devices)\n• Motion sensors: 5% (3 devices)\n\n**Health Status:**\n• Excellent (90-100%): 42 devices\n• Good (70-89%): 8 devices\n• Fair (50-69%): 2 devices\n• Poor (<50%): 0 devices\n\n**Maintenance Recommendations:** 2 devices need attention';

      attachments.push({
        type: 'chart',
        data: {
          type: 'pie',
          title: 'Sensor Distribution with Health Metrics',
          data: [
            { name: 'Temperature', value: 35, health: 92, color: '#3b82f6' },
            { name: 'Humidity', value: 25, health: 88, color: '#10b981' },
            { name: 'Pressure', value: 20, health: 95, color: '#f59e0b' },
            { name: 'Light', value: 15, health: 78, color: '#8b5cf6' },
            { name: 'Motion', value: 5, health: 85, color: '#ec4899' },
          ],
        },
      });
    }

    // Call basic query method for other queries
    else {
      return await this.processBasicQuery(lowerQuery);
    }

    return {
      id: `msg_${Date.now()}`,
      type: 'assistant',
      content: response,
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };
  }

  // Helper methods for advanced analytics
  private detectSeasonality(data: Array<{ value: number; timestamp: string }>, sensorType: string) {
    // Simplified seasonal pattern detection
    const hours = data.map((d) => new Date(d.timestamp).getHours());
    const values = data.map((d) => d.value);

    // Calculate hourly averages
    const hourlyAvg: Record<number, number[]> = {};
    hours.forEach((hour, i) => {
      if (!hourlyAvg[hour]) hourlyAvg[hour] = [];
      hourlyAvg[hour].push(values[i]);
    });

    // Detect anomalous hourly patterns
    let anomalyFactor = 0;
    const currentHour = new Date().getHours();
    const expectedPattern = this.getExpectedSeasonalPattern(sensorType, currentHour);
    const currentAvg = hourlyAvg[currentHour]?.reduce((sum, v) => sum + v, 0) / (hourlyAvg[currentHour]?.length || 1);

    if (Math.abs(currentAvg - expectedPattern) > expectedPattern * 0.2) {
      anomalyFactor = 0.5;
    }

    return { anomalyFactor, expectedPattern, currentAvg };
  }

  private calculateTrend(data: Array<{ value: number; timestamp: string }>) {
    // Simple linear trend calculation
    const values = data.map((d) => d.value);
    const n = values.length;
    const sumX = (n * (n - 1)) / 2;
    const sumY = values.reduce((sum, v) => sum + v, 0);
    const sumXY = values.reduce((sum, v, i) => sum + v * i, 0);
    const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const anomalyFactor = Math.abs(slope) > 0.1 ? Math.abs(slope) : 0;

    return { slope, anomalyFactor };
  }

  private getExpectedSeasonalPattern(sensorType: string, hour: number): number {
    // Mock seasonal patterns based on sensor type
    const patterns: Record<string, (hour: number) => number> = {
      temperature: (h) => 20 + 5 * Math.sin(((h - 6) * Math.PI) / 12),
      humidity: (h) => 50 + 10 * Math.sin(((h - 12) * Math.PI) / 12),
      light: (h) => (h >= 6 && h <= 18 ? 800 - 200 * Math.cos(((h - 12) * Math.PI) / 6) : 0),
      co2: (h) => (h >= 8 && h <= 17 ? 600 + 200 * Math.sin(((h - 12) * Math.PI) / 9) : 400),
    };

    return patterns[sensorType]?.(hour) || 50;
  }

  private analyzePerformanceTrends(sensorData: any[], devices: any[]): AIInsight[] {
    const insights: AIInsight[] = [];

    if (sensorData.length > 0) {
      insights.push({
        id: `trend_${Date.now()}`,
        type: 'trend',
        title: 'Performance Trend Analysis',
        description: 'System performance shows steady improvement over the last 7 days with 12% efficiency gain.',
        confidence: 89,
        severity: 'low',
        actionable: true,
        suggestedActions: ['Continue current optimization strategy', 'Monitor for sustained improvements'],
        affectedDevices: devices.slice(0, 3).map((d) => d.id),
        timestamp: new Date(),
      });
    }

    return insights;
  }

  private analyzeCrossCorrelations(sensorData: any[]): AIInsight[] {
    const insights: AIInsight[] = [];

    if (sensorData.length > 10) {
      insights.push({
        id: `correlation_${Date.now()}`,
        type: 'optimization',
        title: 'Cross-Sensor Correlation Detected',
        description:
          'Strong negative correlation between temperature and humidity sensors suggests HVAC optimization opportunity.',
        confidence: 94,
        severity: 'medium',
        actionable: true,
        suggestedActions: ['Adjust HVAC settings', 'Implement coordinated control'],
        affectedDevices: [],
        timestamp: new Date(),
      });
    }

    return insights;
  }

  private async identifyOptimizationOpportunities(sensorData: any[], devices: any[]): Promise<AIInsight[]> {
    const insights: AIInsight[] = [];

    if (devices.length > 0) {
      insights.push({
        id: `optimization_${Date.now()}`,
        type: 'optimization',
        title: 'Energy Optimization Opportunity',
        description: 'AI analysis indicates 18% energy savings possible through smart scheduling adjustments.',
        confidence: 87,
        severity: 'low',
        actionable: true,
        suggestedActions: ['Implement smart scheduling', 'Optimize polling intervals'],
        affectedDevices: devices.map((d) => d.id),
        timestamp: new Date(),
      });
    }

    return insights;
  }

  private predictFailures(sensorData: any[], devices: any[]): AIInsight[] {
    const insights: AIInsight[] = [];

    if (devices.length > 0 && Math.random() > 0.7) {
      insights.push({
        id: `failure_${Date.now()}`,
        type: 'prediction',
        title: 'Potential Equipment Failure Predicted',
        description: 'Device sensor patterns indicate possible failure within 14 days. Recommend immediate inspection.',
        confidence: 76,
        severity: 'high',
        actionable: true,
        suggestedActions: ['Schedule immediate inspection', 'Prepare replacement parts'],
        affectedDevices: [devices[0].id],
        timestamp: new Date(),
      });
    }

    return insights;
  }
}

export const aiService = new AIService();
